import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LookupType } from 'projects/bis-shared/src/lib/lookup/generic-lookup/lookup-type';
import { map } from 'rxjs/operators';
import { EnvironmentService } from 'projects/bis-shared/src/lib/environment/environment.service';
import { GenericLookupResult } from 'projects/bis-shared/src/lib/lookup/generic-lookup/generic-lookup';
import { HealthcareProvider } from 'projects/bis-shared/src/lib/entities/healthcare-provider';
import { Claimant } from 'projects/bis-shared/src/lib/entities/claimant';
import { Employer } from 'projects/bis-shared/src/lib/entities/employer';
import { MedicalSupplier } from 'projects/bis-shared/src/lib/entities/medical-supplier';
import { ThirdPartyAdministrator } from '../../../../entities/third-party-administrator';
import { InternalApiUrls } from '../../../../utility/apiurls-internal';
import { ExternalApiUrls } from '../../../../utility/apiurls-external';
import { Insurer } from '../../../../entities/insurer';
import { EntityTypeCode } from 'projects/bis-shared/src/lib/utility/system-type-constants';
import { BusinessCardAddress } from 'projects/bis-shared/src/lib/entities/business-card-address';

@Injectable({ providedIn: 'root' })
export class InnerLookupService {
  private environmentService = inject(EnvironmentService);
  private httpClient = inject(HttpClient);
  private internalApiUrls = inject(InternalApiUrls);
  private externalApiUrls = inject(ExternalApiUrls);

  
 

  

  getLookupResult(lookupType: LookupType, id: number): Observable<GenericLookupResult> {
    switch (lookupType) {
      case LookupType.HealthcareProvider:
        return this.lookupHealthcareProvider(id);
      case LookupType.HealthcareProviderSupervisory:
        return this.lookupHealthcareProvider(id);
      case LookupType.Claimant:
        return this.lookupClaimant(id);
      case LookupType.Insurer:
        return this.lookupInsurer(id);
      case LookupType.InsurerExternal:
        return this.lookupInsurerExternal(id);
      case LookupType.Employer:
        return this.lookupEmployer(id);
      case LookupType.MedicalSupplier:
        return this.lookupMedicalSupplier(id);
      case LookupType.ThirdPartyAdministrator:
        return this.lookupThirdPartyAdministrator(id);
      case LookupType.ThirdPartyAdministratorExternal:
        return this.lookupThirdPartyAdministratorExternal(id);
      case LookupType.ExternalSystemUser:
        return this.lookupExternalSystemUser(id);
      case LookupType.SystemUser:
        return this.lookupSystemUser(id);
      default:
        throw new Error('Unknown Lookup Type for preloading info');
    }
  }

  lookupInsurer(id: number): Observable<GenericLookupResult> {
    return this.httpClient.get(this.internalApiUrls.insurer.get(id)).pipe(
      map(
        (result: Insurer) =>
          <GenericLookupResult>{
            id: result.insurerId,
            name: result.insurerName,
            ...result,
          },
      ),
    );
  }

  lookupInsurerExternal(id: number): Observable<GenericLookupResult> {
    return this.httpClient
      .get(this.externalApiUrls.businessCard.get(EntityTypeCode.Insurer, id))
      .pipe(
        map(
          (result: BusinessCardAddress) =>
            <GenericLookupResult>{
              id: result.entityId,
              name: result.displayName,
              ...result,
            },
        ),
      );
  }

  lookupHealthcareProvider(id: number): Observable<GenericLookupResult> {
    const url = this.environmentService.isExternalSite
      ? this.externalApiUrls.healthcareProvider
      : this.internalApiUrls.healthcareProvider;

    return this.httpClient.get(url.get(id)).pipe(
      map(
        (result: HealthcareProvider) =>
          <GenericLookupResult>{
            id: result.healthcareProviderId,
            name: result.lastName + ', ' + result.firstName,
            ...result,
          },
      ),
    );
  }

  lookupMedicalSupplier(id: number): Observable<GenericLookupResult> {
    return this.httpClient.get(this.internalApiUrls.medicalSupplier.get(id)).pipe(
      map(
        (result: MedicalSupplier) =>
          <GenericLookupResult>{
            id: result.medicalSupplierId,
            name: result.medicalSupplierName,
            ...result,
          },
      ),
    );
  }

  lookupClaimant(id: number): Observable<GenericLookupResult> {
    return this.httpClient.get(this.internalApiUrls.claimant.get(id)).pipe(
      map(
        (result: Claimant) =>
          <GenericLookupResult>{
            id: result.claimantId,
            name: result.lastName + ', ' + result.firstName,
            ...result,
          },
      ),
    );
  }

  lookupEmployer(id: number): Observable<GenericLookupResult> {
    const url = this.environmentService.isExternalSite
      ? this.externalApiUrls.employer
      : this.internalApiUrls.employer;
    return this.httpClient.get(url.get(id)).pipe(
      map(
        (result: Employer) =>
          <GenericLookupResult>{
            id: result.employerId,
            name: result.employerName,
            ...result,
          },
      ),
    );
  }

  lookupThirdPartyAdministrator(id: number): Observable<GenericLookupResult> {
    return this.httpClient.get(this.internalApiUrls.thirdPartyAdministrator.get(id)).pipe(
      map(
        (result: ThirdPartyAdministrator) =>
          <GenericLookupResult>{
            id: result.thirdPartyAdministratorId,
            name: result.thirdPartyAdministratorName,
            ...result,
          },
      ),
    );
  }

  lookupThirdPartyAdministratorExternal(id: number): Observable<GenericLookupResult> {
    return this.httpClient.get(this.internalApiUrls.thirdPartyAdministrator.get(id)).pipe(
      map(
        (result: ThirdPartyAdministrator) =>
          <GenericLookupResult>{
            id: result.thirdPartyAdministratorId,
            name: result.thirdPartyAdministratorName,
            ...result,
          },
      ),
    );
  }

  lookupSystemUser(id: number): Observable<GenericLookupResult> {
    return this.httpClient.get(this.internalApiUrls.user.get(id)).pipe(
      map(
        (result: any) =>
          <GenericLookupResult>{
            id: result.systemUserId,
            name: result.userDisplayName,
            ...result,
          },
      ),
    );
  }

  lookupExternalSystemUser(id: number): Observable<GenericLookupResult> {
    const url = this.environmentService.isExternalSite
      ? this.externalApiUrls.user
      : this.internalApiUrls.user;

    return this.httpClient.get(url.get(id)).pipe(
      map(
        (result: any) =>
          <GenericLookupResult>{
            id: result.systemUserId,
            name: result.userDisplayName,
            ...result,
          },
      ),
    );
  }
}
